import { useEffect } from "react";

/**
 * Set per-page <title>, <meta name="description">, <link rel="canonical">,
 * and the matching Open Graph + Twitter Card tags from a React route.
 * Restores the previous values on unmount so navigating between SPA
 * routes never leaves a stale head — critical for search-engine
 * correctness because the static index.html only ships homepage-level
 * metadata, and for social scrapers that don't render JS but *do*
 * re-fetch when you paste a URL into Slack / LinkedIn / iMessage.
 */
export function usePageSeo({ title, description, canonical }) {
  useEffect(() => {
    // ── Helpers ──────────────────────────────────────────────────
    const getMeta = (attr, name) =>
      document.querySelector(`meta[${attr}="${name}"]`);
    const setMeta = (tag, value) => {
      if (tag && value != null) tag.setAttribute("content", value);
    };

    // ── Cache existing values so we can restore on unmount ───────
    const prevTitle = document.title;

    const descTag = getMeta("name", "description");
    const prevDesc = descTag?.getAttribute("content");

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    const createdCanonical = !canonicalTag;
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    const prevCanonical = canonicalTag.getAttribute("href");

    const ogTitleTag = getMeta("property", "og:title");
    const ogDescTag = getMeta("property", "og:description");
    const ogUrlTag = getMeta("property", "og:url");
    const twTitleTag = getMeta("name", "twitter:title");
    const twDescTag = getMeta("name", "twitter:description");

    const prev = {
      ogTitle: ogTitleTag?.getAttribute("content"),
      ogDesc: ogDescTag?.getAttribute("content"),
      ogUrl: ogUrlTag?.getAttribute("content"),
      twTitle: twTitleTag?.getAttribute("content"),
      twDesc: twDescTag?.getAttribute("content"),
    };

    // ── Apply new values ─────────────────────────────────────────
    if (title) {
      document.title = title;
      setMeta(ogTitleTag, title);
      setMeta(twTitleTag, title);
    }
    if (description) {
      setMeta(descTag, description);
      setMeta(ogDescTag, description);
      setMeta(twDescTag, description);
    }
    if (canonical) {
      canonicalTag.setAttribute("href", canonical);
      setMeta(ogUrlTag, canonical);
    }

    return () => {
      document.title = prevTitle;
      if (descTag && prevDesc != null) descTag.setAttribute("content", prevDesc);
      if (createdCanonical && canonicalTag?.parentNode) {
        canonicalTag.parentNode.removeChild(canonicalTag);
      } else if (canonicalTag && prevCanonical) {
        canonicalTag.setAttribute("href", prevCanonical);
      }
      if (ogTitleTag && prev.ogTitle != null) ogTitleTag.setAttribute("content", prev.ogTitle);
      if (ogDescTag && prev.ogDesc != null) ogDescTag.setAttribute("content", prev.ogDesc);
      if (ogUrlTag && prev.ogUrl != null) ogUrlTag.setAttribute("content", prev.ogUrl);
      if (twTitleTag && prev.twTitle != null) twTitleTag.setAttribute("content", prev.twTitle);
      if (twDescTag && prev.twDesc != null) twDescTag.setAttribute("content", prev.twDesc);
    };
  }, [title, description, canonical]);
}

/**
 * Inject a JSON-LD block scoped to the mounted component. Removes it
 * on unmount so per-page schema (FAQPage, etc.) doesn't leak across
 * route changes.
 */
export function usePageSchema(schema) {
  useEffect(() => {
    if (!schema) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.textContent = JSON.stringify(schema);
    document.head.appendChild(s);
    return () => { if (s.parentNode) s.parentNode.removeChild(s); };
  }, [schema]);
}
